(() => {
  const protectedSelector = [
    '[role="dialog"]',
    '[aria-modal="true"]',
    'cart-drawer',
    'menu-drawer',
    'modal-dialog',
    'pickup-availability-drawer',
    '.drawer',
    '.drawer__inner',
    '.cart-drawer',
    '.menu-drawer',
    '.mobile-facets__inner',
    '.predictive-search',
    '.size-guide-modal',
    '.product-media-modal',
  ].join(',');

  const revealSelectors = [
    '.banner__media',
    '.editorial-hero__content',
    '.image-with-text__media-item',
    '.image-with-text__text-item',
    '.multicolumn-card',
    '.collection-list__item',
    '.product-card-wrapper',
    '.article-card',
  ].join(',');

  const imageRevealSelectors = [
    '.banner__media',
    '.card__media',
    '.article-card__image-wrapper',
    '.image-with-text__media',
    '.product__media',
    '.editorial-hero',
  ].join(',');

  const state = {
    revealObserver: null,
    parallaxItems: [],
    parallaxFrame: null,
    reduceMotion: window.matchMedia('(prefers-reduced-motion: reduce)'),
    disableParallax: window.matchMedia('(pointer: coarse), (max-width: 749px)'),
  };

  function initScrollAnimations(root = document) {
    const scope = root instanceof Document ? root : root.ownerDocument || document;
    const container = root instanceof Document ? scope : root;

    prepareRevealElements(container);
    observeRevealElements(container);
    initParallax(container);
  }

  function prepareRevealElements(root) {
    const elements = new Set([
      ...root.querySelectorAll('[data-reveal]'),
      ...root.querySelectorAll(revealSelectors),
      ...root.querySelectorAll(imageRevealSelectors),
    ]);

    elements.forEach((element) => {
      if (shouldSkipElement(element)) return;

      if (element.matches(imageRevealSelectors) && !element.hasAttribute('data-reveal')) {
        element.setAttribute('data-reveal', 'image');
      } else if (!element.hasAttribute('data-reveal')) {
        element.setAttribute('data-reveal', 'fade-up');
      }

      setRevealIndex(element);
    });
  }

  function observeRevealElements(root) {
    const revealItems = Array.from(root.querySelectorAll('[data-reveal]')).filter(
      (element) => !element.classList.contains('is-visible') && !shouldSkipElement(element)
    );

    if (!revealItems.length) return;

    if (state.reduceMotion.matches || !('IntersectionObserver' in window)) {
      revealItems.forEach(showElement);
      return;
    }

    if (!state.revealObserver) {
      state.revealObserver = new IntersectionObserver(onRevealIntersect, {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.12,
      });
    }

    revealItems.forEach((element) => state.revealObserver.observe(element));
  }

  function onRevealIntersect(entries, observer) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      showElement(entry.target);
      observer.unobserve(entry.target);
    });
  }

  function showElement(element) {
    element.classList.add('is-visible');
  }

  function setRevealIndex(element) {
    const listItem = element.closest('.grid__item, li');
    const siblings = listItem?.parentElement ? Array.from(listItem.parentElement.children) : [];
    const index = listItem ? siblings.indexOf(listItem) : 0;

    element.style.setProperty('--reveal-index', Math.max(0, index % 8));
  }

  function initParallax(root) {
    state.parallaxItems = Array.from(document.querySelectorAll('[data-parallax]')).filter(
      (element) => !shouldSkipElement(element)
    );

    if (state.reduceMotion.matches || state.disableParallax.matches || !state.parallaxItems.length) {
      state.parallaxItems.forEach((element) => {
        element.style.transform = '';
      });
      return;
    }

    state.parallaxItems.forEach((element) => {
      if (!root.contains(element) && root !== document) return;
      element.style.setProperty('--parallax-strength', element.dataset.parallax || '0.06');
    });

    window.removeEventListener('scroll', requestParallaxUpdate);
    window.removeEventListener('resize', requestParallaxUpdate);
    window.addEventListener('scroll', requestParallaxUpdate, { passive: true });
    window.addEventListener('resize', requestParallaxUpdate, { passive: true });
    requestParallaxUpdate();
  }

  function requestParallaxUpdate() {
    if (state.parallaxFrame) return;

    state.parallaxFrame = requestAnimationFrame(updateParallax);
  }

  function updateParallax() {
    state.parallaxFrame = null;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    state.parallaxItems.forEach((element) => {
      const rect = element.getBoundingClientRect();

      if (rect.bottom < 0 || rect.top > viewportHeight) return;

      const strength = parseFloat(element.dataset.parallax || '0.06');
      const progress = (viewportHeight - rect.top) / (viewportHeight + rect.height);
      const clamped = Math.min(Math.max(progress, 0), 1);
      const offset = (clamped - 0.5) * rect.height * strength;

      element.style.transform = `translate3d(0, ${offset.toFixed(2)}px, 0)`;
    });
  }

  function shouldSkipElement(element) {
    return element.closest(protectedSelector) || element.closest('[data-no-reveal]');
  }

  window.initScrollAnimations = initScrollAnimations;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initScrollAnimations());
  } else {
    initScrollAnimations();
  }

  if (window.Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (event) => initScrollAnimations(event.target));
  }
})();
